import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { useQueryClient } from '@tanstack/react-query'
import useModals from '@/v2/hooks/useModals'
import { useUpdateAppointment } from '@/v2/hooks/useAppointment'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { getDateTimeWithoutTimezone } from '@/v2/utils/convert'
import { captureSentryException } from '@/v2/utils/sentry'
import RTDailyCalendar from '@/v2/hooks/subscriptions/messages/RTDailyCalendar'
import { Loading } from '../../styles'
import { useDayData, realtimeProps } from '../hooks/useDayData'
import MoveAppointmentDialog from '../Calendar/MoveAppointmentDialog'
import Timeline from './Timeline'
import { getMoveWarning } from './timelineUtils'
import { Root } from './styles'

// Shared API format constants so the move request + optimistic cache patch behave
// identically whatever the renderer is.
const API_STARTS_AT_FORMAT = 'yyyy-M-d hh:mm aa'
const OPTIMISTIC_DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss"

const startMinutesToDate = (date, minutes) => {
  const d = getDateTimeWithoutTimezone(`${date}T00:00`)
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0)
  return d
}

// The appointment field a chair's column stands for. Columns carry the raw
// record id in `value` (see getColumnsByRoom/getColumnsByDoctor), so a drag onto
// another column is just a reassignment of that one field.
const columnAssignment = column =>
  column.type === 'room' ? { roomId: column.value } : { doctorId: column.value }

// Same reassignment against a cached appointment record, so the optimistic patch
// keeps the card in its new column while the request is in flight (getEvents
// derives resourceId from these nested objects).
const columnPatch = (column, appointment) =>
  column.type === 'room'
    ? { room: { ...(appointment.room || {}), id: column.value } }
    : { doctor: { ...(appointment.doctor || {}), id: column.value } }

// Daily timeline used by BOTH desktop and mobile (picked in Daily.jsx by device).
// A custom, drag-native timeline showing every chair side-by-side (fit to width,
// like the old grid) with drag-to-reschedule cards — the same code path on
// pointer and touch, which is why it replaced react-big-calendar's laggy DnD
// addon on desktop. Owns the shared data prep (useDayData), realtime subscription
// (RTDailyCalendar) and move confirmation (MoveAppointmentDialog); `isMobile`
// only tunes the rendering (column/gutter sizing) and the column set (virtual
// doctor columns are desktop-only). A specific chair can be picked from the
// Filterbar, in which case useDayData narrows `columns` to that one.
const DayTimeline = props => {
  const {
    date,
    subjectView,
    typeFilter,
    selectedColumn,
    isMobile,
    appointments,
    timeOffs,
    clinicHours,
    defaultStartHour,
    defaultEndHour,
    loading: loadingData,
    error: errorData,
  } = props

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { goTo } = useModals()
  const { onOpenSnackbar } = useSnackbarContext()
  const { updateAppointment, isLoading: isMoving } = useUpdateAppointment({})

  const {
    columns,
    events,
    offHours,
    loading: loadingDay,
    error: errorDay,
  } = useDayData({
    date,
    subjectView,
    typeFilter,
    selectedColumn,
    isMobile,
    appointments,
    timeOffs,
    clinicHours,
  })

  const [pendingMove, setPendingMove] = useState(null)

  const handleOpenEvent = useCallback(
    calEvent => {
      const ev = calEvent.event
      if (ev.type === 'timeOff') {
        goTo(`/v2/time-off/${ev.id}/edit`, { initialValues: { timeoff: ev } })
      } else {
        goTo(`/v2/appointment/${ev.id}`, {
          initialValues: { appointment: ev },
        })
      }
    },
    [goTo]
  )

  const handleCreate = useCallback(
    (minutes, columnId) => {
      const col = columns.find(c => c.id === columnId)
      if (!col) return
      const start = startMinutesToDate(date, minutes)
      const isVirtual = subjectView === 'room' && col.id.includes('doctor')
      goTo('/v2/event/new', {
        initialValues: { start, resourceId: col.id, virtual: isVirtual },
      })
    },
    [columns, date, subjectView, goTo]
  )

  // A drag reports where it was dropped: a new start minute and the chair's
  // column it landed on. `toColumn` is only set when the chair actually changed,
  // so a plain up/down drag behaves exactly as before.
  const handleMoveRequest = useCallback(
    (calEvent, newStartMin, targetColumnId) => {
      const newStart = new Date(calEvent.start.getTime())
      newStart.setHours(Math.floor(newStartMin / 60), newStartMin % 60, 0, 0)
      const changedColumn = targetColumnId !== calEvent.resourceId
      setPendingMove({
        event: calEvent,
        newStart,
        fromColumn: columns.find(c => c.id === calEvent.resourceId) || null,
        toColumn: changedColumn
          ? columns.find(c => c.id === targetColumnId) || null
          : null,
        warning: getMoveWarning(calEvent.event),
      })
    },
    [columns]
  )

  const handleCancelMove = useCallback(() => setPendingMove(null), [])

  const handleConfirmMove = useCallback(async () => {
    if (!pendingMove) return
    const { event, newStart, toColumn } = pendingMove
    const durationMs = event.end.getTime() - event.start.getTime()
    const newEnd = new Date(newStart.getTime() + durationMs)

    // Optimistically patch the cached daily calendar so the appointment stays in
    // its new slot while the request is in flight; the onSuccess invalidation
    // then reconciles with the server's response.
    const previousDaily = queryClient.getQueryData(['DailyCalendar', date])
    queryClient.setQueryData(['DailyCalendar', date], old => {
      if (!old) return old
      return old.map(day => ({
        ...day,
        appointments: (day.appointments || [])
          .map(appt =>
            appt.id === event.event.id
              ? {
                  ...appt,
                  startsAt: format(newStart, OPTIMISTIC_DATETIME_FORMAT),
                  endsAt: format(newEnd, OPTIMISTIC_DATETIME_FORMAT),
                  ...(toColumn ? columnPatch(toColumn, appt) : {}),
                }
              : appt
          )
          .sort((a, b) => (a.startsAt < b.startsAt ? -1 : 1)),
      }))
    })
    setPendingMove(null)

    const result = await updateAppointment({
      id: event.event.id,
      startsAt: format(newStart, API_STARTS_AT_FORMAT),
      duration: event.event.duration,
      ...(toColumn ? columnAssignment(toColumn) : {}),
    })

    if (result?.error) {
      queryClient.setQueryData(['DailyCalendar', date], previousDaily)
      // Previously this failed silently: the card snapped back with no feedback
      // and nothing reached Sentry, so a broken reschedule was invisible to both
      // the user and us. Tell the user, and record why it failed.
      onOpenSnackbar(
        'Could not move the appointment. Please try again.',
        'error'
      )
      captureSentryException(
        result.error instanceof Error
          ? result.error
          : new Error(`Appointment reschedule failed: ${result.error}`),
        {
          feature: 'reschedule',
          appointment_id: event.event.id,
        }
      )
      return
    }

    onOpenSnackbar('Appointment moved', 'success')
  }, [pendingMove, queryClient, date, updateAppointment, onOpenSnackbar])

  const loading = loadingData || loadingDay
  const error = errorData || errorDay

  if (loading) {
    return (
      <Loading>
        <p>Loading...</p>
      </Loading>
    )
  }
  if (error) {
    navigate('/500')
    return null
  }

  return (
    <Root>
      <RTDailyCalendar {...realtimeProps(date)} />
      <Timeline
        key={`${subjectView}-${date}-${selectedColumn || 'all'}`}
        columns={columns}
        events={events}
        offHours={offHours}
        startMin={defaultStartHour}
        endMin={defaultEndHour}
        date={date}
        isMobile={isMobile}
        showDoctorName={subjectView !== 'doctor'}
        onOpenEvent={handleOpenEvent}
        onMoveRequest={handleMoveRequest}
        onCreate={handleCreate}
      />
      <MoveAppointmentDialog
        isOpen={!!pendingMove}
        loading={isMoving}
        patientName={pendingMove?.event?.event?.patient?.fullName}
        fromTime={pendingMove?.event?.start}
        toTime={pendingMove?.newStart}
        fromColumnTitle={pendingMove?.fromColumn?.title}
        toColumnTitle={pendingMove?.toColumn?.title}
        warning={pendingMove?.warning}
        onConfirm={handleConfirmMove}
        onCancel={handleCancelMove}
      />
    </Root>
  )
}

export default DayTimeline
